import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './FeatureGate.module.scss';

interface FeatureGateProps {
  children: React.ReactNode;
  requiredPlan: 'trial' | 'basic' | 'pro';
  currentPlan?: string;          // can be 'beta'
  currentStatus?: string;        // e.g. 'active'
  requireActive?: boolean;       // block when not active
  featureName: string;
  featureDescription: string;
  showUpgradeButton?: boolean;
}

function normalizePlan(p?: string | null): 'trial' | 'basic' | 'pro' | 'beta' {
  const v = String(p || '').toLowerCase();
  if (v.includes('beta')) return 'beta';
  if (v.includes('pro')) return 'pro';
  if (v.includes('basic')) return 'basic';
  if (v.includes('trial') || v.includes('free')) return 'trial';
  return 'trial';
}

const planLevel: Record<'trial' | 'basic' | 'pro' | 'beta', number> = {
  trial: 0,
  basic: 1,
  pro:   2,
  beta:  3, // ← treat beta as highest tier (>= pro)
};

const FeatureGate: React.FC<FeatureGateProps> = ({
  children,
  requiredPlan,
  currentPlan = 'trial',
  currentStatus,
  requireActive = false,
  featureName,
  featureDescription,
  showUpgradeButton = true,
}) => {
  const navigate = useNavigate();

  const norm = normalizePlan(currentPlan);
  const hasPlan = planLevel[norm] >= planLevel[requiredPlan];
  const isActive = !requireActive || String(currentStatus || '').toLowerCase() === 'active';

  // If beta or sufficient plan + (optionally) active: allow access
  if (hasPlan && isActive) return <>{children}</>;

  const handleUpgrade = () => navigate('/dashboard/subscription');

  // Friendly block UI (kept, but won’t show for beta)
  return (
    <div className={styles.featureGate}>
      <div className={styles.lockIcon}>🔒</div>
      <h3>{featureName}</h3>
      <p className={styles.description}>{featureDescription}</p>

      <div className={styles.planRequirement}>
        <span className={styles.requiredPlan}>Plan requis : {requiredPlan.toUpperCase()}</span>
        <span className={styles.currentPlan}>Votre plan : {norm.toUpperCase()}</span>
        {requireActive && <span className={styles.currentPlan}>Statut : {currentStatus || '—'}</span>}
      </div>

      {showUpgradeButton && (
        <button onClick={handleUpgrade} className={styles.upgradeButton}>
          Upgradez maintenant
        </button>
      )}
    </div>
  );
};

export default FeatureGate;
